import React, { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import StockService from '../../Api_Module/Api_Services/StockService';

interface CompanyResult {
  symbol?: string;
  name?: string;
  exchange?: string;
  ticker?: string;
  label?: string;
}

interface CompanySearchProps {
  placeholder?: string;
  onCompanySelect?: (company: CompanyResult) => void;
  value?: string;
  onChange?: (value: string) => void;
}

const CompanySearch: React.FC<CompanySearchProps> = ({ placeholder = 'Search companies', onCompanySelect, value, onChange }) => {
  const [internalTerm, setInternalTerm] = useState('');
  const term = value !== undefined ? value : internalTerm;
  const setTerm = (newValue: string) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalTerm(newValue);
    }
  };

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompanyResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const q = term.trim();
    if (q.length < 1) {
      setResults([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await StockService.getStockSymbolsSearch(q);
        if (cancelled) return;
        const items: any[] = res?.symbols || res?.data || res || [];
        const mapped: CompanyResult[] = (items || []).map((it: any) => ({
          symbol: it.symbol || it.ticker || it.code || '',
          name: it.name || it.companyName || it.label || '',
          exchange: it.exchange || it.mic || it.primaryExchange || '',
          ticker: it.ticker,
          label: it.label,
        }));
        setResults(mapped);
        setError(null);
      } catch (e: any) {
        setError('Failed to load companies');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [term, open]);

  const handleSelect = (c: CompanyResult) => {
    onCompanySelect?.(c);
    setOpen(false);
    // Keep the selected company name in the input
    setTerm(c.name || c.symbol || '');
  };

  const handleClear = () => {
    setTerm('');
    setResults([]);
    onCompanySelect?.({ symbol: '', name: '' });
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-muted w-5 h-5" />
        <input
          type="text"
          value={term}
          onChange={e => setTerm(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-3 bg-theme-secondary border border-theme-tertiary rounded-lg text-theme-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {term && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-theme-muted hover:text-theme-primary"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-2 bg-theme-primary rounded-lg border border-theme-tertiary shadow-2xl z-50 w-full"
          style={{ maxHeight: 'min(70vh, 560px)', overflow: 'hidden' }}
        >
          <div className="flex-1 p-2 max-h-[48vh] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4B5563 #1F2937' }}>
            {loading && (
              <div className="text-theme-muted text-center py-4 text-sm">Searching…</div>
            )}
            {!loading && error && (
              <div className="text-red-400 text-center py-4 text-sm">{error}</div>
            )}
            {!loading && !error && results.length === 0 && term.trim().length > 0 && (
              <div className="text-theme-muted text-center py-4 text-sm">No companies found</div>
            )}
            {!loading && !error && results.length > 0 && (
              <div className="space-y-1 p-1">
                {results.slice(0, 50).map((c, idx) => (
                  <button
                    key={`${c.symbol}-${idx}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(c)}
                    className="w-full text-left px-3 py-2 rounded hover:bg-theme-secondary border border-transparent hover:border-theme-tertiary transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-theme-primary text-sm truncate">{c.name || c.symbol}</div>
                        <div className="text-theme-muted text-xs truncate">{c.symbol}{c.exchange ? ` • ${c.exchange}` : ''}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySearch;
